import axios from 'axios';
import { fromJS } from 'immutable';
import i18next from '../translations/i18nConfig';
import { clearAuthData, getAuthData, setAuthData } from './authData';
import { currentPartner } from './currentPartner';

// Every request that gates the UI (sign-in, the boot profile fetch, page
// data) would otherwise wait forever on a stalled connection. Uploads opt out
// per call with `timeout: 0` (see UPLOAD_REQUEST_OPTIONS in api.ts).
export const REQUEST_TIMEOUT_MS = 30_000;

// Hostnames served by the Yandex VPS. Their API calls go through eks2, the
// nginx pass-through on that VPS. Every other production host talks to eks
// directly — a host missing from this list must never end up on the relay.
const VPS_HOSTS = [
  'app2.sabologistics.com',
  '23industrial.com',
  'www.23industrial.com',
];

const getAxiosParams = () => {
  if (currentPartner.api?.baseUrl) {
    return { baseURL: currentPartner.api.baseUrl };
  }

  const { origin } = window.location;
  // development — use dev-server origin so /client_api is same-origin (see server.proxy in vite.config.ts)
  if (origin.includes('localhost'))
    return {
      baseURL: `${origin}/client_api`,
    };

  // staging
  if (
    origin.includes('192.168') ||
    origin.includes('staging') ||
    origin.includes('beta-app') ||
    origin.includes('brocoders.sklad')
  )
    return {
      baseURL: 'https://eks.sabollc.com/client_api',
      headers: {
        Authorization: 'Basic c2tsYWQ6b3p6a2xs',
      },
    };
  // production
  const { hostname } = window.location;
  let prodHost = 'eks.tapegun.me';

  if (hostname === 'oae.sabologistics.com') {
    prodHost = 'oae-api.tapegun.me';
  } else if (VPS_HOSTS.includes(hostname)) {
    prodHost = 'eks2.tapegun.me';
  }

  return {
    baseURL: `https://${prodHost}/client_api`,
    headers: {
      Authorization: 'Basic c2tsYWQ6T29wZXF6ejEK',
    },
  };
};

const axiosInstance = axios.create({
  ...getAxiosParams(),
  timeout: REQUEST_TIMEOUT_MS,
  // Report a timeout as ETIMEDOUT instead of sharing ECONNABORTED with aborts.
  transitional: { clarifyTimeoutError: true },
});

axiosInstance.interceptors.request.use((request) => {
  Object.assign(request.headers, getAuthData());
  return request;
});

axiosInstance.interceptors.response.use(
  (response) => {
    const { request, headers, data } = response;

    if (request.responseURL.includes('/auth') && headers['access-token']) {
      setAuthData(headers);
    }

    return fromJS({ headers, data });
  },
  (errorObject) => {
    const { response, message, code } = errorObject;
    const { pathname } = window.location;

    if (!response) {
      return Promise.reject({
        message:
          code === 'ETIMEDOUT'
            ? i18next.t('error.requestTimeout', { ns: 'common' })
            : `Network error: ${message || 'No response from server'}`,
        status: null,
        data: null,
      });
    }

    const { headers, data, status } = response;

    if (
      status === 401 &&
      !(pathname.includes('signin') || pathname.includes('signup'))
    )
      // session expired mid-use — hard navigation also resets the redux store
      clearAuthData().finally(() => window.location.replace('/signin'));

    if (headers && headers['access-token']) setAuthData(headers);

    return Promise.reject({ data, message, status });
  },
);

export const getServerOrigin = () =>
  axiosInstance.defaults.baseURL.replace('/client_api', '');

export default axiosInstance;
